import { yupResolver } from "@hookform/resolvers/yup";
import { Button as MUIButton } from "@mui/material";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { getUserActionState } from "app/reducers/userSlice";
import {
  activateUser,
  deactivateUser,
  editUser,
  fetchUserById,
} from "app/thunks/userAPI";
import classNames from "classnames";
import { ConfirmationModal } from "features/confirmation-modal/ConfirmationModal";
import { NAVIGATOR } from "features/navigation/index";
import { EditUserRequest } from "interfaces/requests/Users";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useHistory, useParams } from "react-router";
import {
  BUTTON_STYLES,
  BUTTON_TYPES,
  EditFormIcon,
  HeaderWithBack,
  Input,
  Spinner,
  Switch,
  useCustomToast,
} from "ui";
import { SessionsForm } from "ui/Sessions/SessionsForm";
import { buildConfigs } from "utils/form/sessions-form";

import { SelectedContainersState, SessionContainers, UserData } from "../types";
import { userSchema } from "../validations";

export const EditUserForm = () => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();

  const dispatch = useAppDispatch();
  const { isLoading, users, isFetching } = useAppSelector(getUserActionState);
  const [isEditing, setIsEditing] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { showSuccessToast, showErrorToast } = useCustomToast();

  const isUserActive = users[0]?.isActive;

  const [selectedContainers, setSelectedContainers] =
    useState<SelectedContainersState>({
      session1: {},
      session2: {},
    });

  useEffect(() => {
    if (users.length > 0 && users[0]?.configs?.length) {
      const transformedConfigs: SelectedContainersState =
        users[0].configs.reduce((acc, config, index) => {
          const sessionKey = `session${index + 1}`;

          acc[sessionKey] = config.containerSlots.reduce(
            (slotsAcc, slot, slotIndex) => {
              if (slot.containerId) {
                const selectKey = `select-${slotIndex}`;
                slotsAcc[selectKey] =
                  slot.containerId?.number?.toString() || "unassigned";
              }
              return slotsAcc;
            },
            {} as SessionContainers,
          );

          return acc;
        }, {} as SelectedContainersState);

      setSelectedContainers(transformedConfigs);
    }
  }, [users]);

  const values = useMemo(() => {
    if (users.length > 0 && users[0]?.configs) {
      return {
        name: users[0]?.name || "",
        email: users[0]?.email || "",
        phoneNumber: users[0]?.phoneNumber || "",
        country: users[0]?.country || "",
        city: users[0]?.city || "",
        address: users[0]?.address || "",
        fiscalId: users[0]?.fiscalId || 0,
        slotsAmount: users[0]?.slotsAmount || 0,
        containersAmount: users[0]?.containersAmount || 0,
      };
    }
  }, [users]);

  const {
    handleSubmit,
    formState: { errors, isValid, isDirty },
    register,
    watch,
  } = useForm<UserData>({
    mode: "all",
    resolver: yupResolver(userSchema),
    values,
  });

  const containersAmount = watch("containersAmount");

  useEffect(() => {
    if (id) {
      dispatch(fetchUserById(id));
    }
  }, [dispatch, id]);

  const handleBack = () => {
    history.push(NAVIGATOR.users);
  };

  const isSession1Valid = useMemo(() => {
    return Object.values(selectedContainers.session1).some((value) => value);
  }, [selectedContainers.session1]);

  const isSessionChanged = useMemo(() => {
    if (!users.length || !users[0]?.configs) return false;

    return Object.entries(selectedContainers).some(
      ([sessionKey, containers]) => {
        const sessionIndex = parseInt(sessionKey.replace("session", "")) - 1;
        const originalSession =
          users[0]?.configs?.[sessionIndex]?.containerSlots || [];

        return Object.entries(containers).some(([selectKey, value]) => {
          const slotIndex = parseInt(selectKey.replace("select-", ""));
          const originalContainer =
            originalSession?.[slotIndex]?.containerId?.number?.toString() ||
            "unassigned";
          return value !== originalContainer;
        });
      },
    );
  }, [selectedContainers, users]);

  const isSubmitDisabled =
    !isValid ||
    isLoading ||
    !isEditing ||
    !isSession1Valid ||
    (!isDirty && !isSessionChanged);

  const onSubmit = async (data: UserData) => {
    const configs = buildConfigs(selectedContainers);
    const userData: EditUserRequest = {
      id,
      ...data,
      configs,
    };

    try {
      await dispatch(editUser(userData)).unwrap();
      history.push(NAVIGATOR.users);
      showSuccessToast({
        title: "¡Listo!",
        message: "Pudimos editar la cuenta.",
      });
    } catch (_error) {
      setErrorMessage();
    }
  };

  const onActivateOrDeactivate = async () => {
    if (!id) return;
    try {
      if (isUserActive) {
        await dispatch(deactivateUser(id)).unwrap();
      } else {
        await dispatch(activateUser(id)).unwrap();
      }
      showSuccessToast({
        title: "¡Listo!",
        message: `La cuenta se ha ${isUserActive ? "desactivado" : "activado"}.`,
      });
      setIsModalOpen(false);
    } catch (_) {
      setErrorMessage();
    }
  };

  const handleWhiteSpaces = (event: any) => {
    event.target.value = event.target.value.replace(/\s+/g, "");
  };

  const setErrorMessage = () => {
    showErrorToast({
      title: "Lo sentimos",
      message: "No pudimos editar la cuenta",
    });
  };

  if (isFetching || isLoading) {
    return (
      <div className="flex w-full justify-center items-center bg-faux-dark-gunmetal h-screen">
        <Spinner size={60} />
      </div>
    );
  }

  return (
    <>
      <div className="flex-col w-full min-h-screen px-14 pt-16 pb-8 bg-faux-dark-gunmetal">
        <HeaderWithBack text={`${users[0]?.name}`} onBackClick={handleBack} />

        <div className="flex justify-between mb-4">
          <div className="flex items-center">
            <p className="text-lg font-semibold">Datos del cliente</p>
          </div>
          <div className="flex gap-12">
            <div
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => setIsEditing(!isEditing)}
            >
              <EditFormIcon width="24px" height="24px" />
              <p className="text-lg font-semibold">Editar</p>
            </div>
            <div className="flex border rounded-lg px-4 items-center gap-4">
              <p className="text-lg">{`Cuenta ${isUserActive ? "Activa" : "Inactiva"}`}</p>
              <Switch
                sx={{ m: 1 }}
                defaultChecked={isUserActive}
                checked={isUserActive}
                onClick={() => setIsModalOpen(true)}
              />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-x-[6%] gap-y-6 mb-8">
          <Input
            {...register("name")}
            name="name"
            label="Nombre comercial"
            errorMessage={errors.name?.message}
            disabled={!isEditing}
          />
          <Input
            {...register("email")}
            name="email"
            label="Email"
            errorMessage={errors.email?.message}
            disabled
          />
          <Input
            {...register("phoneNumber")}
            name="phoneNumber"
            label="Teléfono"
            errorMessage={errors.phoneNumber?.message}
            disabled={!isEditing}
            onInput={handleWhiteSpaces}
          />
          <Input
            {...register("country")}
            name="country"
            label="País"
            errorMessage={errors.country?.message}
            disabled={!isEditing}
          />
          <Input
            {...register("city")}
            name="city"
            label="Ciudad"
            errorMessage={errors.city?.message}
            disabled={!isEditing}
          />
          <Input
            {...register("address")}
            name="address"
            label="Dirección"
            errorMessage={errors.address?.message}
            disabled={!isEditing}
          />
          <Input
            {...register("fiscalId")}
            name="fiscalId"
            label="Dato fiscal"
            errorMessage={errors.fiscalId?.message}
            disabled={!isEditing}
          />
        </div>

        <p className="mb-6 text-lg font-semibold">Datos de Licencia</p>
        <div className="grid grid-cols-3 gap-x-[6%]">
          <Input
            {...register("slotsAmount")}
            name="slotsAmount"
            label="Cascos habilitados"
            errorMessage={errors.slotsAmount?.message}
            disabled={!isEditing}
          />
          <Input
            {...register("containersAmount")}
            name="containersAmount"
            label="Contenedores habilitados"
            errorMessage={errors.containersAmount?.message}
            disabled={!isEditing}
            onChange={(e) => {
              register("containersAmount").onChange(e);
              setSelectedContainers({
                session1: {},
                session2: {},
              });
            }}
          />
        </div>

        <SessionsForm
          containersAmount={containersAmount}
          selectedContainers={selectedContainers}
          setSelectedContainers={setSelectedContainers}
          disabled={!isEditing}
        />

        {isEditing && (
          <div>
            <div className="flex flex-col items-end mt-4 pb-3.5">
              <div className="mb-4">
                {!isSession1Valid && (
                  <p className="text-sm text-red-500">
                    Debe seleccionar al menos un contenedor en la Sesión 1.
                  </p>
                )}
              </div>
              <MUIButton
                variant="contained"
                disabled={isSubmitDisabled}
                onClick={handleSubmit(onSubmit)}
                className={classNames(BUTTON_STYLES[BUTTON_TYPES.CONFIRM])}
                type="submit"
              >
                {isLoading ? <Spinner color="white" /> : <p>Guardar</p>}
              </MUIButton>
            </div>
          </div>
        )}
        {isModalOpen && (
          <ConfirmationModal
            closeButtonType={BUTTON_TYPES.CANCEL}
            confirmButtonType={BUTTON_TYPES.CONFIRM}
            closeText="Cancelar"
            confirmText={isUserActive ? "Desactivar" : "Activar"}
            title={isUserActive ? "Desactivar Cuenta" : "Activar Cuenta"}
            label={`Vas a ${isUserActive ? "desactivar" : "activar"} esta cuenta, ¿quieres continuar?`}
            callback={onActivateOrDeactivate}
            callbackClose={() => setIsModalOpen(false)}
          />
        )}
      </div>
    </>
  );
};
